import styled from '@emotion/styled';
import { Button, Dialog, DialogContent } from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import { useMemo, useState } from 'react';

import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { useTranslation, useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties
} from '../common';
import {
  disconnectGreenhouseV3,
  getGreenhouseV3AuthorizeUrl,
  useGreenhouseV3Connected
} from './service';

const NAME = 'Greenhouse';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const SetupDialog = ({
  open,
  onClose,
  orgId,
  onSuccessfulIntegration
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
} & IntegrationSetupDialogProperties) => {
  const { t } = useTranslation();
  const { t: ut } = useUnsafeTranslation();

  const [connected, loading, error] = useGreenhouseV3Connected(orgId);

  const [submitError, setSubmitError] = useState<string | null>(null);

  if (loading && !error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitleWithTypography>
          {NAME} {t('org.integrations.integration')}
        </DialogTitleWithTypography>
        <Content>
          <Skeleton />
          <Skeleton />
        </Content>
        <DialogActionsWithSlots
          right={
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
          }
        />
      </Dialog>
    );
  }

  if (!connected) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitleWithTypography>
          {NAME} {t('org.integrations.integration')}
        </DialogTitleWithTypography>
        <Content>
          <Alert severity="info" icon={false}>
            {ut('Connect Greenhouse and Dado to import your employee data')}
          </Alert>

          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Content>
        <DialogActionsWithSlots
          right={
            <>
              <Button color="primary" onClick={onClose}>
                {t('Cancel')}
              </Button>
              <ButtonWithPromise
                variant="contained"
                color="primary"
                onClick={async () => {
                  setSubmitError(null);
                  try {
                    // TODO(analytics): fire a PA "Integration Connection
                    // Started" event with 'Integration Type': 'GREENHOUSE_V3'
                    // once the shared PAIntegrationConnectionStartedEvent union
                    // includes it. See the rippling card for the pattern.
                    const url = await getGreenhouseV3AuthorizeUrl(orgId);
                    // Full-page redirect to Greenhouse's consent screen. The
                    // backend handles the callback and redirects back to the
                    // /integrations/oauth/greenhouse-v3 landing route, so we
                    // never resolve the connection inline here.
                    window.location.href = url;
                  } catch (err: any) {
                    setSubmitError(
                      err?.message ||
                        ut('Could not start the Greenhouse connection.')
                    );
                  }
                }}
                pending={`${t('org.integrations.actions.redirecting')}...`}
              >
                {ut('Connect with Greenhouse')}
              </ButtonWithPromise>
            </>
          }
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>

      <Content>
        <Alert severity="info" icon={false}>
          {ut('Connect Greenhouse and Dado to import your employee data')}
        </Alert>
        <Alert severity="success">{ut('Connected')}</Alert>
      </Content>

      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={async () => {
              await disconnectGreenhouseV3(orgId);
              // TODO(analytics): fire a PA "Integration Disconnected" event with
              // 'Integration Type': 'GREENHOUSE_V3' once the shared
              // PAIntegrationDisconnectedEvent union includes it.
              // `connected` flips via the configInfos listener.
            }}
            pending={`${ut('Disconnecting')}...`}
          >
            {ut('Disconnect')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

export const GreenhouseV3SettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { org } = useCurrentUser();
  const [connected] = useGreenhouseV3Connected(org.id);
  const active = useMemo(() => connected, [connected]);

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/greenhouse.svg"
      active={active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          orgId={org.id}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
